import '../styles/Policies/PrivacyPolicy.css';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    id: 'overview',
    title: 'Overview',
    content: `Actyra ("we", "our", or "us") is an independent actuarial research and advisory firm providing high-impact research, insight publications, and consulting services to insurers, pension funds, and regulators — particularly within African and emerging markets. This Privacy Policy explains how we collect, use, and protect information when you interact with our website or services.`,
  },
  {
    id: 'information-collected',
    title: 'Information We Collect',
    subsections: [
      {
        heading: 'Information You Provide',
        items: [
          'Name and professional title when you contact us or submit enquiries',
          'Email address when you subscribe to our research publications or newsletters',
          'Organisation name and industry context provided in advisory or consulting enquiries',
          'Communications sent to us via email or contact forms',
        ],
      },
      {
        heading: 'Information Collected Automatically',
        items: [
          'Anonymised usage data such as pages visited and time spent on our site',
          'Browser type, device type, and general geographic region',
          'Referring URLs and navigation paths for improving content relevance',
        ],
        note: 'We do not use cookies or persistent tracking technologies on this website.',
      },
    ],
  },
  {
    id: 'how-we-use',
    title: 'How We Use Your Information',
    items: [
      'To deliver actuarial research publications, whitepapers, and insight briefs you have subscribed to',
      'To respond to enquiries regarding IFRS 17 advisory, capital and solvency consulting, model validation, or product and profitability analysis',
      'To improve the quality and relevance of our research outputs and public dashboards',
      'To send industry commentary, conference papers, or regulatory response submissions where you have opted in',
      'To comply with applicable legal and regulatory obligations',
    ],
  },
  {
    id: 'legal-basis',
    title: 'Legal Basis for Processing',
    items: [
      'Consent — where you have explicitly opted in to receive research publications or communications',
      'Legitimate interests — to operate and improve our research platform and advisory services',
      'Contractual necessity — where processing is required to fulfil a consulting engagement or service agreement',
      'Legal obligation — where processing is required by applicable law or regulation',
    ],
  },
  {
    id: 'data-sharing',
    title: 'Data Sharing',
    content: `Actyra does not sell, rent, or trade your personal information to third parties. We may share data in limited circumstances:`,
    items: [
      'With professional service providers (e.g., hosting, analytics) under strict data processing agreements',
      'With research collaborators, only on an anonymised or aggregated basis',
      'Where required by law, regulation, or court order',
      'In connection with a merger, acquisition, or sale of Actyra, where data protections will be maintained',
    ],
  },
  {
    id: 'retention',
    title: 'Data Retention',
    content: `We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law. Subscription data is held for the duration of the subscription and up to 12 months thereafter. Enquiry data is retained for up to 24 months unless a consulting engagement requires longer retention.`,
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    content: `Depending on your jurisdiction, you may have the following rights with respect to your personal data:`,
    items: [
      'Access — the right to obtain a copy of your personal data held by us',
      'Rectification — the right to correct inaccurate or incomplete data',
      'Erasure — the right to request deletion of your personal data',
      'Restriction — the right to limit how we process your data in certain circumstances',
      'Objection — the right to object to processing based on legitimate interests',
      'Portability — the right to receive your data in a structured, machine-readable format',
    ],
    footer: 'To exercise any of these rights, contact us at info@actyra.co.ke.',
  },
  {
    id: 'security',
    title: 'Data Security',
    content: `We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, or disclosure. These include secure transmission protocols, access controls, and regular security assessments. No system is entirely immune to risk, and we encourage you to contact us immediately if you believe your data may have been compromised.`,
  },
  {
    id: 'updates',
    title: 'Updates to This Policy',
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. The effective date will be updated accordingly. Material changes will be communicated to subscribers via email.`,
  },
  {
    id: 'contact',
    title: 'Contact',
    content: `For any questions or concerns regarding this Privacy Policy or the handling of your personal data, please contact:\n\nActyra — Privacy Office\nEmail: info@actyra.co.ke`,
  },
];

/* ── Decorative Elements ────────────────────────────────────────── */
function PolicyOrnament() {
  return (
    <div className="pp-ornament" aria-hidden="true">
      <svg viewBox="0 0 200 200" className="pp-ornament-svg">
        <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(15,158,138,0.08)" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(15,158,138,0.06)" strokeWidth="0.5" strokeDasharray="4 4" />
        <circle cx="100" cy="100" r="40" fill="none" stroke="rgba(15,158,138,0.1)" strokeWidth="0.5" />
        <line x1="20" y1="100" x2="180" y2="100" stroke="rgba(15,158,138,0.06)" strokeWidth="0.5" />
        <line x1="100" y1="20" x2="100" y2="180" stroke="rgba(15,158,138,0.06)" strokeWidth="0.5" />
      </svg>
    </div>
  );
}

function SectionMarker({ num }) {
  return (
    <div className="pp-section-marker" aria-hidden="true">
      <span className="pp-marker-num">{String(num).padStart(2, '0')}</span>
      <span className="pp-marker-line" />
      <span className="pp-marker-dot" />
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────── */
export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('');
  const [progress, setProgress] = useState(0);
  const sectionRefs = useRef([]);
  const headerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress((scrollTop / docHeight) * 100);
    };

    // Section observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pp-revealed');
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-80px 0px -40% 0px' }
    );

    sectionRefs.current.forEach((el) => el && observer.observe(el));
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const effectiveDate = 'May 2025';

  return (
    <div className="pp-root">
      {/* Progress bar */}
      <div className="pp-progress-track">
        <div className="pp-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Background architecture */}
      <div className="pp-bg" aria-hidden="true">
        <div className="pp-bg-noise" />
        <div className="pp-bg-grid" />
        <div className="pp-bg-line pp-bg-line--left" />
        <div className="pp-bg-line pp-bg-line--right" />
        <div className="pp-bg-orb pp-bg-orb--tl" />
        <div className="pp-bg-orb pp-bg-orb--br" />
        <div className="pp-bg-orb pp-bg-orb--center" />
      </div>

      {/* Hero Header */}
      <header className="pp-hero" ref={headerRef}>
        <div className="pp-hero-inner">
          <Link to="/" className="pp-back">
            <span className="pp-back-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </span>
            <span className="pp-back-text">Back to Actyra</span>
          </Link>

          <div className="pp-hero-meta">
            <span className="pp-hero-tag">Legal</span>
            <span className="pp-hero-divider" />
            <span className="pp-hero-date">Effective {effectiveDate}</span>
          </div>

          <h1 className="pp-hero-title">
            <span className="pp-title-line">Privacy</span>
            <span className="pp-title-line pp-title-line--accent">Policy</span>
          </h1>

          <p className="pp-hero-lead">
            How Actyra collects, uses, and protects your information
            <span className="pp-lead-accent"> — with the same rigour we apply to actuarial science.</span>
          </p>

          <div className="pp-hero-badges">
            <span className="pp-badge">
              <span className="pp-badge-dot" />
              No cookies used
            </span>
            <span className="pp-badge">
              <span className="pp-badge-dot pp-badge-dot--green" />
              GDPR Compliant
            </span>
            <span className="pp-badge">
              <span className="pp-badge-dot pp-badge-dot--crimson" />
              Data Minimisation
            </span>
          </div>
        </div>

        <PolicyOrnament />
      </header>

      {/* Main Layout */}
      <div className="pp-layout">
        {/* Floating Sidebar */}
        <aside className="pp-sidebar">
          <nav className="pp-toc" aria-label="Policy contents">
            <div className="pp-toc-header">
              <span className="pp-toc-label">Contents</span>
              <span className="pp-toc-count">{sections.length} sections</span>
            </div>
            <div className="pp-toc-list">
              {sections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={`pp-toc-link ${activeSection === s.id ? 'pp-toc-link--active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <span className="pp-toc-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="pp-toc-text">{s.title}</span>
                  <span className="pp-toc-bar">
                    <span className="pp-toc-bar-fill" />
                  </span>
                </a>
              ))}
            </div>
          </nav>
        </aside>

        {/* Content Body */}
        <main className="pp-body">
          {sections.map((section, i) => (
            <article
              key={section.id}
              id={section.id}
              className="pp-article pp-reveal"
              ref={(el) => (sectionRefs.current[i] = el)}
            >
              <SectionMarker num={i + 1} />

              <div className="pp-article-content">
                <h2 className="pp-article-title">{section.title}</h2>

                {section.content && (
                  <div className="pp-prose">
                    {section.content.split('\n\n').map((para, j) => (
                      <p key={j} className="pp-paragraph">{para}</p>
                    ))}
                  </div>
                )}

                {section.subsections &&
                  section.subsections.map((sub, si) => (
                    <div key={si} className="pp-subsection">
                      <h3 className="pp-subsection-heading">{sub.heading}</h3>
                      <ul className="pp-list">
                        {sub.items.map((item, ii) => (
                          <li key={ii} className="pp-list-item">
                            <span className="pp-list-bullet">
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            </span>
                            <span className="pp-list-text">{item}</span>
                          </li>
                        ))}
                      </ul>
                      {sub.note && (
                        <div className="pp-callout">
                          <span className="pp-callout-icon">◈</span>
                          <p className="pp-callout-text">{sub.note}</p>
                        </div>
                      )}
                    </div>
                  ))}

                {section.items && (
                  <ul className="pp-list">
                    {section.items.map((item, ii) => (
                      <li key={ii} className="pp-list-item">
                        <span className="pp-list-bullet">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </span>
                        <span className="pp-list-text">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.footer && (
                  <div className="pp-footer-block">
                    <span className="pp-footer-line" />
                    <p className="pp-footer-text">{section.footer}</p>
                  </div>
                )}
              </div>
            </article>
          ))}

          {/* Bottom CTA */}
          <div className="pp-bottom-cta">
            <div className="pp-cta-content">
              <h3 className="pp-cta-title">Questions about your data?</h3>
              <p className="pp-cta-text">
                Our Privacy Office is available to address any concerns regarding 
                your personal information and how we handle it.
              </p>
              <a href="mailto:privacy@actyra.com" className="pp-cta-btn">
                <span>Contact Privacy Office</span>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Footer */}
          <footer className="pp-page-footer">
            <span>© 2025 Actyra. All rights reserved.</span>
            <span className="pp-footer-dot">·</span>
            <Link to="/terms" className="pp-footer-link">Terms of Use</Link>
            <span className="pp-footer-dot">·</span>
            <Link to="/data-usage" className="pp-footer-link">Data Usage Policy</Link>
          </footer>
        </main>
      </div>
    </div>
  );
}
